import * as React from 'react';
import { useContext, useEffect, useState } from "react";
import { AdminProjectContext } from "../../../contexts/AdminProjectContext";
import { CommonContext } from "../../../contexts/CommonContext";
import useAxios from "../../../hooks/useAxios";
import { UserInterface } from "../../../interface/User.interface";
import { ADMIN_PROJECT_PARTICIPANTS_URL, NEW_PARTICIPANT_URL, USER_LIST_URL } from "../../../utils/constant";
import { Alert, MenuItem, Select, Snackbar, TextField, Avatar, Button, Modal, IconButton } from "@mui/material";
import { Close, PersonAdd, Group } from "@mui/icons-material";
import styled from "styled-components";

// Styled components
const StyledModal = styled(Modal)`display: flex; align-items: center; justify-content: center;`;
const ModalContent = styled.div`background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(20px); border-radius: 20px; border: 1px solid rgba(255, 255, 255, 0.3); box-shadow: 0 20px 60px rgba(0,0,0,0.15); width: 700px; max-width: 90vw; max-height: 90vh; overflow: hidden;`;
const ModalHeader = styled.div`display:flex; align-items:center; justify-content:space-between; padding:1.5rem 2rem; background: linear-gradient(45deg, var(--primary-color), var(--secondary-color)); color:white; border-radius:20px 20px 0 0;`;
const HeaderLeft = styled.div`display:flex; align-items:center; gap:1rem;`;
const ProjectAvatar = styled(Avatar)`&&{ width:50px; height:50px; background: rgba(255,255,255,0.2); border:2px solid rgba(255,255,255,0.3); }`;
const ModalTitle = styled.div`h3{margin:0; font-size:1.3rem; font-weight:600; color:white;} p{margin:0.25rem 0 0 0; font-size:0.9rem; color: rgba(255,255,255,0.8);}`;
const CloseButton = styled(IconButton)`&&{ color:white; background: rgba(255,255,255,0.1); border:1px solid rgba(255,255,255,0.2); &:hover{background: rgba(255,255,255,0.2); transform: rotate(90deg);}}`;
const ModalBody = styled.div`padding:2rem; height: 400px; overflow-y: auto;`;
const FormSection = styled.div`margin-bottom:2rem;`;
const SectionTitle = styled.h4`font-size:1rem; font-weight:600; color:var(--text-primary); margin:0 0 1rem 0; display:flex; align-items:center; gap:0.5rem;`;
const StyledSelect = styled(Select)`&&{ margin-bottom:1.5rem; }`;
const StyledTextField = styled(TextField)`&&{ margin-bottom:1.5rem; }`;
const SharesGrid = styled.div`display:grid; grid-template-columns: repeat(auto-fit, minmax(200px,1fr)); gap:1rem; margin-top:1rem;`;
const ButtonContainer = styled.div`display:flex; gap:1rem; justify-content:flex-end; margin-top:2rem; padding-top:1.5rem; border-top:1px solid rgba(0,0,0,0.05);`;
const AddButton = styled(Button)`&&{ background: linear-gradient(45deg, var(--primary-color), var(--secondary-color)); color:white; border-radius:12px; padding:0.75rem 1.5rem; font-weight:600; text-transform:none; }`;
const AlertContainer = styled.div`margin-bottom:1rem;`;
const ProjectInfo = styled.div`background: rgba(102,126,234,0.05); border-radius:12px; padding:1rem; margin-bottom:1.5rem; border-left:4px solid var(--primary-color);`;

export enum ProjectRole {
    PARTICIPANT = 'PARTICIPANT',
    MANAGER = 'MANAGER'
}

export interface Participant extends UserInterface {
    role: ProjectRole;
    joinedAt: Date;
    totalShare: number;
    shareConfigurations: { withdrawalRank: number; share: number }[];
    upToDate?: boolean;
}

export default function AdminAddProjectParticipantsModal(props: { openModal: boolean, setOpenModal: Function }) {
    const { selectedProject, refreshProjects } = useContext(AdminProjectContext);
    const { setNeedToRefreshData } = useContext(CommonContext);
    const { getWithAxios, putWithAxios } = useAxios();

    const [users, setUsers] = useState<UserInterface[]>([]);
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserInterface | null>(null);
    const [share, setShare] = useState<number>(1);
    const [withdrawalRanks, setWithdrawalRanks] = useState<number[]>([]);
    const [takenRanks, setTakenRanks] = useState<number[]>([]);
    const [globalError, setGlobalError] = useState('');
    const [globalInfo, setGlobalInfo] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Charger participants du projet (seulement quand le modal est ouvert)
    useEffect(() => {
        if (!props.openModal) return;

        const projectId = selectedProject?.getProjectId();
        if (!projectId) return;

        getWithAxios(ADMIN_PROJECT_PARTICIPANTS_URL(projectId))
            .then((res: { success: boolean; participants: Participant[] }) => {
                if (res.success && Array.isArray(res.participants)) {
                    setParticipants(res.participants);
                    const ranks: number[] = res.participants.flatMap((p: Participant) =>
                        p.shareConfigurations.map((sc) => sc.withdrawalRank)
                    );
                    setTakenRanks(ranks);
                } else {
                    setParticipants([]);
                    setTakenRanks([]);
                }
            })
            .catch(err => setGlobalError(err.message));
    }, [props.openModal, selectedProject]);

    // Charger tous les utilisateurs (seulement quand le modal est ouvert)
    useEffect(() => {
        if (!props.openModal) return;

        getWithAxios(USER_LIST_URL)
            .then(res => setUsers(Array.isArray(res.data) ? res.data : []))
            .catch(err => setGlobalError('Erreur lors du chargement des utilisateurs'));
    }, [props.openModal]);

    // Filtrer les non-participants
    const nonParticipants = users.filter(user =>
        !participants.some(p => p.uuid === user.uuid) && user.role?.toUpperCase() !== "ADMIN"
    );

    // Update withdrawalRanks à chaque changement de share
    useEffect(() => {
        setWithdrawalRanks(Array(share).fill(0));
    }, [share]);

    const onClose = () => props.setOpenModal(false);
    const hasDuplicate = withdrawalRanks.some((rank, i) => {
        const otherRanks = withdrawalRanks.filter((_, idx) => idx !== i);
        return rank <= 0 || takenRanks.includes(rank) || otherRanks.includes(rank);
    });
    const addNewParticipant = async () => {
        if (isSubmitting) return;

        const projectId = selectedProject?.getProjectId();
        if (!projectId || !selectedUser || !selectedUser.uuid) return;

        // Remplacer 0 par les prochains rangs libres
        const newRanks = withdrawalRanks.map(rank => {
            if (rank > 0) return rank;
            let nextRank = 1;
            while (takenRanks.includes(nextRank) || withdrawalRanks.includes(nextRank)) {
                nextRank++;
            }
            return nextRank;
        });

        // Vérification doublons
        const duplicates = newRanks.filter(rank => takenRanks.includes(rank) || newRanks.filter(r => r === rank).length > 1);

        if (duplicates.length > 0) {
            setGlobalError(`Les rangs suivants sont déjà attribués ou invalides : ${duplicates.join(", ")}`);
            return;
        }

        setIsSubmitting(true);
        setGlobalError('');

        try {
            await putWithAxios(NEW_PARTICIPANT_URL(projectId), {
                userId: selectedUser.uuid,
                share,
                withdrawalRanks
            });

            setGlobalInfo(`${selectedUser.firstname} ajouté avec succès`);
            setNeedToRefreshData(true);

            // Rafraîchir la liste des projets avec les participants
            await refreshProjects();

            onClose();
        } catch (err: any) {
            const msg = err?.response?.data?.message || err.message || "Les rangs choisis sont déjà attribués";
            setGlobalError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <StyledModal open={props.openModal} onClose={() => onClose()}>
            <ModalContent>
                <ModalHeader>
                    <HeaderLeft>
                        <ProjectAvatar><Group /></ProjectAvatar>
                        <ModalTitle>
                            <h3>Nouvel Adhésion</h3>
                            <p>Ajouter un participant au projet</p>
                            {globalError && <AlertContainer><Alert severity="error">{globalError}</Alert></AlertContainer>}
                        </ModalTitle>

                    </HeaderLeft>
                    <CloseButton onClick={onClose}><Close /></CloseButton>
                </ModalHeader>

                <ModalBody>

                    {selectedProject && (
                        <>
                            <ProjectInfo>
                                <strong>Projet sélectionné :</strong> {selectedProject.name}<br/>
                                <small>Vous vous apprêtez à ajouter un utilisateur à ce projet</small>
                            </ProjectInfo>

                            <FormSection>
                                <SectionTitle><PersonAdd /> Sélectionner l'utilisateur</SectionTitle>
                                <StyledSelect
                                    fullWidth
                                    value={selectedUser?.uuid || ''}
                                    displayEmpty
                                    onChange={(e) => {
                                        const user = nonParticipants.find(u => u.uuid === e.target.value);
                                        setSelectedUser(user || null);
                                    }}
                                >
                                    <MenuItem value="" disabled>Choisir un utilisateur</MenuItem>
                                    {nonParticipants.map(user => (
                                        <MenuItem key={user.uuid} value={user.uuid}>{user.lastname} {user.firstname}</MenuItem>
                                    ))}
                                </StyledSelect>
                            </FormSection>

                            {selectedUser && (
                                <FormSection>
                                    <SectionTitle>Configuration des parts</SectionTitle>
                                    <StyledTextField
                                        type="number"
                                        fullWidth
                                        value={share}
                                        label="Nombre de parts"
                                        inputProps={{ min: 1 }}
                                        error={share <= 0}
                                        helperText={share <= 0 ? "Nombre de part invalide" : ""}
                                        onChange={e => setShare(parseInt(e.target.value))}
                                    />

                                    {share > 0 && (
                                        <>
                                            <SectionTitle>Positions de retrait</SectionTitle>
                                            <SharesGrid>
                                                {withdrawalRanks.map((rank, i) => {
                                                    const otherRanks = withdrawalRanks.filter((_, idx) => idx !== i);
                                                    const isDuplicate = rank <= 0 || takenRanks.includes(rank) || otherRanks.includes(rank);

                                                    return (
                                                        <StyledTextField
                                                            key={i}
                                                            type="number"
                                                            label={`Position ${i + 1}`}
                                                            value={rank}
                                                            inputProps={{ min: 1 }}
                                                            error={isDuplicate}
                                                            helperText={
                                                                rank <= 0 ? "Position invalide"
                                                                    : takenRanks.includes(rank) ? "Rang déjà pris"
                                                                        : otherRanks.includes(rank) ? "Rang déjà utilisé dans ce formulaire"
                                                                            : ""
                                                            }
                                                            onChange={e => {
                                                                const newRanks = [...withdrawalRanks];
                                                                newRanks[i] = parseInt(e.target.value) || 0;
                                                                setWithdrawalRanks(newRanks);
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </SharesGrid>
                                        </>
                                    )}

                                    <ButtonContainer>
                                        <AddButton
                                            disabled={!selectedUser || share <= 0 || withdrawalRanks.includes(0) || hasDuplicate || isSubmitting}
                                            onClick={addNewParticipant}
                                        >
                                            {isSubmitting ? 'Ajout en cours...' : 'Ajouter au projet'}
                                        </AddButton>
                                    </ButtonContainer>
                                </FormSection>
                            )}
                        </>
                    )}
                </ModalBody>
            </ModalContent>
        </StyledModal>
    );
}
